import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteContact, getContacts } from '../../../../../api/endpoints/cmsSite';
import type { SiteContact } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ContactFormDialog } from '../ContactFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header). Contact is scoped to a parent Site via `site_Id`; this page
 *  is only ever rendered as a tab inside `SiteDetailPage` for one specific site, so `siteId`
 *  comes in as a fixed prop. */
export default function ContactListPage({ siteId }: { siteId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SiteContact | null>(null);
  const [deleting, setDeleting] = useState<SiteContact | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'contacts', siteId],
    queryFn: () => getContacts(siteId),
  });
  const rows: SiteContact[] = useMemo(() => query.data?.contacts ?? [], [query.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteContact(id),
    onSuccess: () => {
      enqueueSnackbar('İletişim kaydı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'contacts'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">İletişim</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni İletişim
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Başlık</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Adres</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı iletişim kaydı bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.title ?? '—'}</TableCell>
                <TableCell>{row.phone ?? '—'}</TableCell>
                <TableCell>{row.email ?? '—'}</TableCell>
                <TableCell>{row.address ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContactFormDialog open={createOpen} contact={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <ContactFormDialog open={!!editing} contact={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="İletişim kaydını sil"
        description={`"${deleting?.title ?? ''}" iletişim kaydını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
